// Central Zustand store — PSD §11, P1.17. State layer.
// Composes all slices with persist middleware routing to IDB/localStorage.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import type {
  AllocationAssumptions,
  CapTable,
  ComplianceInputs,
  DiscountInputs,
  EquityValueAnchor,
  ShareInstrument,
  ValuationInputs,
  ValuationResult,
  ValuationStatus,
} from '@backsolve/shared-types';
import { runValuation as engineRunValuation, ENGINE_VERSION } from '@backsolve/shared-logic';
import type { AuditEvent, Alert, SignOffRecord, ValuationRecord } from '../persistence/schema';
import { toIsoTimestamp } from '@backsolve/shared-logic/dates';
import { ulid } from '@/id';
import { saveValuation, listValuationsByEntity } from '../persistence/valuationRepo';
import { saveCapTable, listCapTablesByEntity } from '../persistence/capTableRepo';
import { saveActiveValuationId, loadActiveValuationId, defaultSettings } from '../persistence/settingsStorage';
import {
  emptyCapTable,
  defaultValuationInputs,
  defaultAssumptions,
  defaultEquityValue,
  defaultDiscounts,
  defaultCompliance,
} from './defaults';
import { computeStepValidation, allPriorStepsValid } from './builderValidation';
import { idbStateStorage } from '../persistence/idbPersistAdapter';
import type { SettingsRecord } from '../persistence/schema';
import type { DemoDataset } from '@backsolve/shared-logic/demoDatasets';

// ── Toast type ──────
export interface Toast {
  id: string;
  kind: 'info' | 'error' | 'success';
  message: string;
}

export type ScreenId =
  | 'dashboard'
  | 'capTable'
  | 'builder'
  | 'results'
  | 'analysis'
  | 'report'
  | 'settings';

export interface Scenario {
  id: string;
  name: string;
  variant: 'base' | 'conservative' | 'aggressive' | 'custom';
  financing: 'pre' | 'post';
  overrides: Partial<AllocationAssumptions & DiscountInputs>;
  result: ValuationResult | null;
}

// ── Store shape ──────
export interface AppState {
  // capTable slice
  capTablesByEntity: Record<string, CapTable[]>;
  activeCapTableId: string | null;
  draftCapTable: CapTable | null;
  addInstrument: (entity: string, inst: ShareInstrument) => void;
  updateInstrument: (entity: string, id: string, patch: Partial<ShareInstrument>) => void;
  removeInstrument: (entity: string, id: string) => void;
  setLiquidationTerms: (entity: string, id: string, patch: Partial<ShareInstrument>) => void;
  commitSnapshot: (entity: string, cap: CapTable) => void;
  selectSnapshot: (entity: string, capId: string) => void;
  loadCapTablesForEntity: (entity: string) => Promise<void>;

  // valuation slice
  valuations: Record<string, ValuationRecord>;
  activeValuationId: string | null;
  builderCurrentStep: number;
  builderCompletedSteps: number[];
  createValuation: (entityName: string) => string;
  setActiveValuation: (id: string | null) => void;
  setCompliance: (valuationId: string, patch: Partial<ComplianceInputs>) => void;
  setEquityValue: (valuationId: string, patch: Partial<EquityValueAnchor>) => void;
  setAssumptions: (valuationId: string, patch: Partial<AllocationAssumptions>) => void;
  setDiscounts: (valuationId: string, patch: Partial<DiscountInputs>) => void;
  setCapTableForValuation: (valuationId: string, cap: CapTable) => void;
  setStepField: (valuationId: string, step: number, field: string, value: unknown) => void;
  advanceStep: () => void;
  gotoStep: (step: number) => void;
  runValuation: (valuationId: string) => Promise<void>;
  loadValuations: (entityName: string) => Promise<void>;

  // results slice
  resultByValuation: Record<string, ValuationResult>;
  runStatus: 'idle' | 'running' | 'done' | 'error';
  runError: string | null;
  clearResult: (valuationId: string) => void;

  // ui slice
  activeScreen: ScreenId;
  toastQueue: Toast[];
  returnIntent: { screen: ScreenId; valuationId: string } | null;
  navigate: (screen: ScreenId) => void;
  setReturnIntent: (intent: { screen: ScreenId; valuationId: string } | null) => void;
  pushToast: (kind: Toast['kind'], message: string) => void;
  dismissToast: (id: string) => void;

  // settings slice
  settings: SettingsRecord;
  setTheme: (theme: 'light' | 'dark') => void;
  setRbacRole: (role: SettingsRecord['rbacRole']) => void;
  toggleClientSideProcessing: () => void;
  setActiveEntity: (entity: string | null) => void;
  setDefaults: (patch: Partial<SettingsRecord>) => void;

  // compliance slice (P2)
  alertsByValuation: Record<string, Alert[]>;
  signOffByValuation: Record<string, SignOffRecord | null>;
  refreshAlerts: (valuationId: string) => void;
  acknowledgeAlert: (valuationId: string, alertId: string) => void;
  registerMaterialEvent: (valuationId: string, message: string) => void;
  submitSignOff: (valuationId: string, annotations: string, approved: boolean) => void;

  // audit slice (P2)
  eventsByValuation: Record<string, AuditEvent[]>;
  logEvent: (
    valuationId: string,
    action: string,
    before: unknown,
    after: unknown,
    source: string,
  ) => void;

  // scenarios slice (P2)
  scenariosByValuation: Record<string, Scenario[]>;
  whatIfOverride: Partial<AllocationAssumptions & DiscountInputs> | null;
  addScenario: (valuationId: string, scenario: Scenario) => void;
  removeScenario: (valuationId: string, scenarioId: string) => void;
  clearScenarios: (valuationId: string) => void;
  setWhatIfOverride: (override: Partial<AllocationAssumptions & DiscountInputs> | null) => void;
  clearWhatIf: () => void;

  // demo datasets slice (Stage 10)
  /** Load a demo dataset: write it into the valuations map as a fresh
   *  ValuationRecord, set it active, sync the entity, run the engine, and
   *  land on the Results screen. Returns the new valuationId. */
  loadDemoDataset: (dataset: DemoDataset) => string;
  /** Reset all entity/valuation/result state to empty defaults. Settings
   *  (theme, role, etc.) are preserved so the user's environment is intact. */
  clearDemoData: () => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // ── capTable slice ──────
      capTablesByEntity: {},
      activeCapTableId: null,
      draftCapTable: null,
      addInstrument: (entity, inst) =>
        set((s) => {
          const draft = s.draftCapTable ?? emptyCapTable(entity);
          return { draftCapTable: { ...draft, instruments: [...draft.instruments, inst] } };
        }),
      updateInstrument: (entity, id, patch) =>
        set((s) => {
          const draft = s.draftCapTable ?? emptyCapTable(entity);
          return {
            draftCapTable: {
              ...draft,
              instruments: draft.instruments.map((i) => (i.id === id ? { ...i, ...patch } : i)),
            },
          };
        }),
      removeInstrument: (entity, id) =>
        set((s) => {
          const draft = s.draftCapTable ?? emptyCapTable(entity);
          return {
            draftCapTable: { ...draft, instruments: draft.instruments.filter((i) => i.id !== id) },
          };
        }),
      setLiquidationTerms: (entity, id, patch) => get().updateInstrument(entity, id, patch),
      commitSnapshot: (entity, cap) => {
        const existing = get().capTablesByEntity[entity] ?? [];
        const version = existing.length > 0 ? Math.max(...existing.map((c) => c.version)) + 1 : 1;
        const snapshot: CapTable = { ...cap, version, id: cap.id || `cap_${ulid()}` };
        const updated = [...existing.filter((c) => c.id !== snapshot.id), snapshot].sort(
          (a, b) => b.version - a.version,
        );
        set((s) => ({
          capTablesByEntity: { ...s.capTablesByEntity, [entity]: updated },
          activeCapTableId: snapshot.id,
          draftCapTable: snapshot,
        }));
        void saveCapTable(snapshot);
      },
      selectSnapshot: (entity, capId) => {
        const list = get().capTablesByEntity[entity] ?? [];
        const found = list.find((c) => c.id === capId) ?? null;
        set({ activeCapTableId: capId, draftCapTable: found });
      },
      loadCapTablesForEntity: async (entity) => {
        const list = await listCapTablesByEntity(entity);
        set((s) => ({ capTablesByEntity: { ...s.capTablesByEntity, [entity]: list } }));
      },

      // ── valuation slice ──────
      valuations: {},
      activeValuationId: null,
      builderCurrentStep: 1,
      builderCompletedSteps: [],
      createValuation: (entityName) => {
        const cap = get().draftCapTable ?? emptyCapTable(entityName);
        const inputs = defaultValuationInputs(entityName, cap);
        const now = toIsoTimestamp(new Date());
        const record: ValuationRecord = {
          valuationId: inputs.valuationId,
          entityName,
          status: 'draft',
          inputs,
          result: null,
          report: null,
          signOff: null,
          alerts: [],
          createdAt: now,
          updatedAt: now,
        };
        set((s) => ({
          valuations: { ...s.valuations, [record.valuationId]: record },
          activeValuationId: record.valuationId,
          builderCurrentStep: 1,
          builderCompletedSteps: [],
          activeScreen: 'builder',
        }));
        saveActiveValuationId(record.valuationId);
        void saveValuation(record);
        return record.valuationId;
      },
      setActiveValuation: (id) => {
        set({ activeValuationId: id });
        saveActiveValuationId(id);
      },
      setCompliance: (valuationId, patch) =>
        mutateInput(set, get, valuationId, (inputs) => ({
          ...inputs,
          compliance: { ...inputs.compliance, ...patch },
        })),
      setEquityValue: (valuationId, patch) =>
        mutateInput(set, get, valuationId, (inputs) => ({
          ...inputs,
          equityValue: { ...inputs.equityValue, ...patch },
        })),
      setAssumptions: (valuationId, patch) =>
        mutateInput(set, get, valuationId, (inputs) => ({
          ...inputs,
          assumptions: { ...inputs.assumptions, ...patch },
        })),
      setDiscounts: (valuationId, patch) =>
        mutateInput(set, get, valuationId, (inputs) => ({
          ...inputs,
          discounts: { ...inputs.discounts, ...patch },
        })),
      setCapTableForValuation: (valuationId, cap) =>
        mutateInput(set, get, valuationId, (inputs) => ({ ...inputs, capTableSnapshot: cap })),
      setStepField: (valuationId, _step, field, value) => {
        // Generic field setter routed to the right sub-object by known prefixes.
        const record = get().valuations[valuationId];
        if (!record) return;
        const inputs = record.inputs;
        if (field in inputs.compliance) {
          get().setCompliance(valuationId, { [field]: value } as Partial<ComplianceInputs>);
        } else if (field in inputs.equityValue) {
          get().setEquityValue(valuationId, { [field]: value } as Partial<EquityValueAnchor>);
        } else if (field in inputs.assumptions) {
          get().setAssumptions(valuationId, { [field]: value } as Partial<AllocationAssumptions>);
        } else if (field in inputs.discounts) {
          get().setDiscounts(valuationId, { [field]: value } as Partial<DiscountInputs>);
        }
      },
      advanceStep: () => {
        const rec = activeRecord(get());
        const next = get().builderCurrentStep + 1;
        if (next > 7) return;
        if (!allPriorStepsValid(rec, get().builderCurrentStep)) return;
        set((s) => ({
          builderCurrentStep: next,
          builderCompletedSteps: Array.from(new Set([...s.builderCompletedSteps, s.builderCurrentStep])),
        }));
      },
      gotoStep: (step) => {
        const rec = activeRecord(get());
        if (step < 1 || step > 7) return;
        if (step > 1 && !allPriorStepsValid(rec, step - 1)) return;
        set({ builderCurrentStep: step });
      },
      runValuation: async (valuationId) => {
        const record = get().valuations[valuationId];
        if (!record) return;
        set({ runStatus: 'running', runError: null });
        try {
          // assemble inputs
          const inputs = record.inputs;
          const raw = engineRunValuation(inputs);
          // stamp computedAt + engineVersion (engine leaves blank)
          const now = toIsoTimestamp(new Date());
          const result: ValuationResult = { ...raw, computedAt: now, engineVersion: ENGINE_VERSION };
          const status: ValuationStatus =
            result.reconciled && result.solver.converged ? 'converged' : 'failed';
          const updated: ValuationRecord = {
            ...record,
            status,
            result,
            updatedAt: now,
          };
          // persist BEFORE exposing result to UI
          await saveValuation(updated);
          set((s) => ({
            valuations: { ...s.valuations, [valuationId]: updated },
            resultByValuation: { ...s.resultByValuation, [valuationId]: result },
            runStatus: 'done',
          }));
          get().refreshAlerts(valuationId);
          get().logEvent(valuationId, 'valuation.run', null, { converged: result.solver.converged, reconciled: result.reconciled }, 'store');
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          set({ runStatus: 'error', runError: message });
          get().pushToast('error', `Valuation failed: ${message}`);
        }
      },
      loadValuations: async (entityName) => {
        const list = await listValuationsByEntity(entityName);
        const map: Record<string, ValuationRecord> = {};
        for (const v of list) map[v.valuationId] = v;
        set((s) => ({ valuations: { ...s.valuations, ...map } }));
        const active = get().activeValuationId ?? loadActiveValuationId();
        if (active) set({ activeValuationId: active });
      },

      // ── results slice ──────
      resultByValuation: {},
      runStatus: 'idle',
      runError: null,
      clearResult: (valuationId) =>
        set((s) => {
          const next = { ...s.resultByValuation };
          delete next[valuationId];
          return { resultByValuation: next };
        }),

      // ── ui slice ──────
      activeScreen: 'dashboard',
      toastQueue: [],
      returnIntent: null,
      navigate: (screen) => {
        set({ activeScreen: screen });
        // Sync URL hash so calc-sidebar's <SidebarNav> (useHashNav) reflects
        // code-driven navigation. pushState + popstate dispatch mirrors the
        // component's internal setActiveItem behaviour.
        if (typeof window !== 'undefined') {
          window.history.pushState(null, '', `#nav/${screen}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      },
      setReturnIntent: (intent) => set({ returnIntent: intent }),
      pushToast: (kind, message) => {
        const id = `toast_${ulid()}`;
        set((s) => ({ toastQueue: [...s.toastQueue, { id, kind, message }] }));
        setTimeout(() => get().dismissToast(id), 5000);
      },
      dismissToast: (id) =>
        set((s) => ({ toastQueue: s.toastQueue.filter((t) => t.id !== id) })),

      // ── settings slice ──────
      settings: { ...defaultSettings },
      setTheme: (theme) =>
        set((s) => ({ settings: { ...s.settings, theme } })),
      setRbacRole: (role) =>
        set((s) => ({ settings: { ...s.settings, rbacRole: role } })),
      toggleClientSideProcessing: () =>
        set((s) => ({
          settings: { ...s.settings, clientSideProcessing: !s.settings.clientSideProcessing },
        })),
      setActiveEntity: (entity) =>
        set((s) => ({ settings: { ...s.settings, activeEntityName: entity } })),
      setDefaults: (patch) =>
        set((s) => ({ settings: { ...s.settings, ...patch } })),

      // ── compliance slice (P2) ──────
      alertsByValuation: {},
      signOffByValuation: {},
      refreshAlerts: (valuationId) => {
        const rec = get().valuations[valuationId];
        if (!rec) return;
        const now = toIsoTimestamp(new Date());
        const alerts: Alert[] = [...(get().alertsByValuation[valuationId] ?? [])];
        // refresh-due
        if (rec.result) {
          const ageMs = Date.now() - new Date(rec.result.computedAt || rec.updatedAt).getTime();
          if (ageMs > 365 * 24 * 60 * 60 * 1000) {
            if (!alerts.some((a) => a.kind === 'refreshDue')) {
              alerts.push({
                id: `alert_${ulid()}`,
                kind: 'refreshDue',
                message: 'Last valuation is more than 12 months old — a refresh is due.',
                raisedAt: now,
                acknowledged: false,
              });
            }
          }
        }
        // anchor flags
        if (rec.inputs.equityValue.transactionDate) {
          const ageMs =
            Date.now() - new Date(rec.inputs.equityValue.transactionDate).getTime();
          if (ageMs > 365 * 24 * 60 * 60 * 1000) {
            if (!alerts.some((a) => a.kind === 'anchorStale')) {
              alerts.push({
                id: `alert_${ulid()}`,
                kind: 'anchorStale',
                message: 'Anchor transaction is older than 12 months.',
                raisedAt: now,
                acknowledged: false,
              });
            }
          }
        }
        if (!rec.inputs.equityValue.armLengthFlag) {
          if (!alerts.some((a) => a.kind === 'anchorNotArmsLength')) {
            alerts.push({
              id: `alert_${ulid()}`,
              kind: 'anchorNotArmsLength',
              message: 'Anchor transaction is flagged as not arm\u2019s-length.',
              raisedAt: now,
              acknowledged: false,
            });
          }
        }
        set((s) => ({ alertsByValuation: { ...s.alertsByValuation, [valuationId]: alerts } }));
      },
      acknowledgeAlert: (valuationId, alertId) =>
        set((s) => ({
          alertsByValuation: {
            ...s.alertsByValuation,
            [valuationId]: (s.alertsByValuation[valuationId] ?? []).map((a) =>
              a.id === alertId ? { ...a, acknowledged: true } : a,
            ),
          },
        })),
      registerMaterialEvent: (valuationId, message) => {
        const now = toIsoTimestamp(new Date());
        const alert: Alert = {
          id: `alert_${ulid()}`,
          kind: 'materialEvent',
          message,
          raisedAt: now,
          acknowledged: false,
        };
        set((s) => ({
          alertsByValuation: {
            ...s.alertsByValuation,
            [valuationId]: [...(s.alertsByValuation[valuationId] ?? []), alert],
          },
        }));
      },
      submitSignOff: (valuationId, annotations, approved) => {
        const role = get().settings.rbacRole;
        const canApprove = role === 'reviewer' || role === 'approver';
        const rec = get().valuations[valuationId];
        const result = rec?.result;
        // Guard: refuse to finalize an approval when the run did not converge or
        // did not reconcile — the underlying FMV is not reliable. Rejections are
        // always permitted (a reviewer may always dissent).
        if (approved && result && (!result.solver.converged || !result.reconciled)) {
          get().logEvent(
            valuationId,
            'signoff.blocked',
            null,
            { approved, reason: 'non-converged' },
            'store',
          );
          get().pushToast('error', 'Cannot finalize: valuation did not converge or reconcile.');
          return;
        }
        const now = toIsoTimestamp(new Date());
        const signOff: SignOffRecord = {
          signedOffAt: now,
          signedOffBy: role,
          role: canApprove ? role : 'reviewer',
          annotations,
          approved,
        };
        const status: ValuationStatus =
          approved && canApprove ? 'finalized' : rec?.status ?? 'draft';
        set((s) => {
          const updated = rec
            ? { ...s.valuations, [valuationId]: { ...rec, signOff, status, updatedAt: now } }
            : s.valuations;
          return {
            signOffByValuation: { ...s.signOffByValuation, [valuationId]: signOff },
            valuations: updated,
          };
        });
        const persisted = get().valuations[valuationId];
        if (persisted) {
          void saveValuation({ ...persisted, signOff, status });
        }
        get().logEvent(valuationId, 'signoff.submit', null, { approved, role }, 'store');
      },

      // ── audit slice (P2) ──────
      eventsByValuation: {},
      logEvent: (valuationId, action, before, after, source) => {
        const now = toIsoTimestamp(new Date());
        const evt: AuditEvent = {
          eventId: `evt_${ulid()}`,
          valuationId,
          timestamp: now,
          actor: get().settings.rbacRole,
          action,
          before,
          after,
          source,
        };
        set((s) => ({
          eventsByValuation: {
            ...s.eventsByValuation,
            [valuationId]: [...(s.eventsByValuation[valuationId] ?? []), evt],
          },
        }));
      },

      // ── scenarios slice (P2) ──────
      scenariosByValuation: {},
      whatIfOverride: null,
      addScenario: (valuationId, scenario) =>
        set((s) => ({
          scenariosByValuation: {
            ...s.scenariosByValuation,
            [valuationId]: [...(s.scenariosByValuation[valuationId] ?? []), scenario],
          },
        })),
      removeScenario: (valuationId, scenarioId) =>
        set((s) => ({
          scenariosByValuation: {
            ...s.scenariosByValuation,
            [valuationId]: (s.scenariosByValuation[valuationId] ?? []).filter(
              (sc) => sc.id !== scenarioId,
            ),
          },
        })),
      clearScenarios: (valuationId) =>
        set((s) => ({
          scenariosByValuation: { ...s.scenariosByValuation, [valuationId]: [] },
        })),
      setWhatIfOverride: (override) => set({ whatIfOverride: override }),
      clearWhatIf: () => set({ whatIfOverride: null }),

      // ── demo datasets slice (Stage 10) ──────
      loadDemoDataset: (dataset) => {
        const inputs = dataset.inputs;
        // Avoid id collisions with any existing loaded demo / user valuations
        // by stamping a fresh ULID-suffixed valuationId; preserve the dataset's
        // cap-table snapshot (engine reads entity name off it).
        const valuationId = `${dataset.id}_${ulid()}`;
        const capWithEntity = {
          ...inputs.capTableSnapshot,
          id: `${inputs.capTableSnapshot.id}_${ulid()}`,
        };
        const inputsWithFreshIds: ValuationInputs = {
          ...inputs,
          valuationId,
          capTableSnapshot: capWithEntity,
        };
        const now = toIsoTimestamp(new Date());
        const entityName = capWithEntity.entityName;
        const record: ValuationRecord = {
          valuationId,
          entityName,
          status: 'draft',
          inputs: inputsWithFreshIds,
          result: null,
          report: null,
          signOff: null,
          alerts: [],
          createdAt: now,
          updatedAt: now,
        };
        // Run the engine synchronously so the loaded state is immediately
        // useful (and so demo-state assertions in tests don't require an
        // await). Failures are surfaced as a toast and a status of 'failed'.
        let result: ValuationResult | null = null;
        let status: ValuationStatus = 'draft';
        try {
          const raw = engineRunValuation(inputsWithFreshIds);
          result = { ...raw, computedAt: now, engineVersion: ENGINE_VERSION };
          status = result.reconciled && result.solver.converged ? 'converged' : 'failed';
        } catch {
          status = 'failed';
        }
        const finalRecord: ValuationRecord = { ...record, status, result, updatedAt: now };
        set((s) => ({
          valuations: { ...s.valuations, [valuationId]: finalRecord },
          activeValuationId: valuationId,
          resultByValuation: result
            ? { ...s.resultByValuation, [valuationId]: result }
            : s.resultByValuation,
          capTablesByEntity: {
            ...s.capTablesByEntity,
            [entityName]: [capWithEntity, ...(s.capTablesByEntity[entityName] ?? [])],
          },
          activeCapTableId: capWithEntity.id,
          draftCapTable: capWithEntity,
          builderCurrentStep: 1,
          builderCompletedSteps: [],
          settings: { ...s.settings, activeEntityName: entityName },
          activeScreen: 'results',
          runStatus: status === 'converged' ? 'done' : 'error',
        }));
        saveActiveValuationId(valuationId);
        void saveValuation(finalRecord);
        void saveCapTable(capWithEntity);
        return valuationId;
      },
      clearDemoData: () => {
        // Preserve user environment (theme, role, etc.) but drop all entity /
        // valuation / result state so the app returns to its empty onboarding
        // surface. showLoadDemo is preserved by design (the user just used it).
        set((s) => ({
          valuations: {},
          activeValuationId: null,
          resultByValuation: {},
          capTablesByEntity: {},
          activeCapTableId: null,
          draftCapTable: null,
          alertsByValuation: {},
          signOffByValuation: {},
          eventsByValuation: {},
          scenariosByValuation: {},
          whatIfOverride: null,
          builderCurrentStep: 1,
          builderCompletedSteps: [],
          runStatus: 'idle',
          runError: null,
          settings: { ...s.settings, activeEntityName: null },
          activeScreen: 'dashboard',
        }));
        saveActiveValuationId(null);
      },
    }),
    {
      name: 'backsolve',
      storage: createJSONStorage(() => idbStateStorage),
      partialize: (s) => ({
        capTablesByEntity: s.capTablesByEntity,
        activeCapTableId: s.activeCapTableId,
        valuations: s.valuations,
        activeValuationId: s.activeValuationId,
        settings: s.settings,
        eventsByValuation: s.eventsByValuation,
        scenariosByValuation: s.scenariosByValuation,
        signOffByValuation: s.signOffByValuation,
        alertsByValuation: s.alertsByValuation,
      }),
    },
  ),
);

// ── helpers ──────
function activeRecord(state: AppState): ValuationRecord | null {
  const id = state.activeValuationId;
  if (!id) return null;
  return state.valuations[id] ?? null;
}

type SetFn = (
  partial: AppState | Partial<AppState> | ((s: AppState) => AppState | Partial<AppState>),
) => void;

function mutateInput(
  set: SetFn,
  get: () => AppState,
  valuationId: string,
  fn: (inputs: ValuationInputs) => ValuationInputs,
) {
  const record = get().valuations[valuationId];
  if (!record) return;
  const before = record.inputs;
  const inputs = fn(before);
  const updated: ValuationRecord = {
    ...record,
    inputs,
    updatedAt: toIsoTimestamp(new Date()),
  };
  set((s) => ({
    valuations: { ...s.valuations, [valuationId]: updated },
  }));
  // clear cached result on input mutation
  get().clearResult(valuationId);
  // audit log
  get().logEvent(valuationId, 'input.mutate', before, inputs, 'store');
}

export { shallow, computeStepValidation, allPriorStepsValid };
export { defaultAssumptions, defaultEquityValue, defaultDiscounts, defaultCompliance, emptyCapTable };
